import { useState, useEffect } from "react";
import { useField } from "formik";
import { useCurrencyFormatter } from "components/FormattedMoney/useCurrencyFormatter";
import { useGetRawCurrencyValue } from "components/FormattedMoney/useGetRawCurrencyValue";
import { useSettings } from "utils/settingsContext";
import { useLanguageContext } from "utils/LanguageProvider";
import ReactCurrencyInput from "react-currency-input-field";
import { Input, FormControl, FormErrorMessage, useColorMode } from "@chakra-ui/react";

const getInitialInputDisplay = (amount, fractions) => {
  fractions = fractions === 0 ? 0 : (fractions || 2); // need to handle intentional 0 value, otherwise if falsy then default to 2
  const finalAmount = amount / 10 ** fractions;
  return finalAmount;
};

const CurrencyInput = (props) => {
  const [field, meta] = useField(props);
  const { integratorSettings } = useSettings();
  const { locale } = useLanguageContext();
  const { colorMode } = useColorMode();
  const [isTouched, setIsTouched] = useState(false);

  const currency = props.currency;
  const currencyFormatter = useCurrencyFormatter(currency);
  const getRawCurrencyValue = useGetRawCurrencyValue(currency);
  const virtualCurrency = (integratorSettings?.virtualCurrencies?.currencies || {})[currency];

  let decimalsLimit = null;

  if (virtualCurrency) {
    decimalsLimit = virtualCurrency.minor_unit; // number of digits of decimals
  }

  const intlConfig = {
    locale,
    style: "currency",
    signDisplay: "auto",
    currency,
  };

  const error = props.error || (isTouched && meta.error);

  const [inputValue, setInputValue] = useState(field.value || null);
  const [inputDisplay, setInputDisplay] = useState(
    field.value ? getInitialInputDisplay(field.value, decimalsLimit) : null
  );

  useEffect(() => {
    const delayInputTimeoutId = setTimeout(() => {
      field.onChange({ target: { name: field.name, value: inputValue } });
    }, 500);
    return () => clearTimeout(delayInputTimeoutId);
  }, [inputValue]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <Input
        {...props}
        placeholder={currencyFormatter(0)}
        _placeholder={
          colorMode === "dark" ? { opacity: 1, color: 'gray.200' } : {}
        }
        role={`${field.name}-currency-input`} // eslint-disable-line jsx-a11y/aria-role
        intlConfig={intlConfig}
        allowNegativeValue={false}
        prefix={virtualCurrency ? virtualCurrency.symbol || virtualCurrency.currency : null}
        decimalsLimit={decimalsLimit}
        as={ReactCurrencyInput}
        value={inputDisplay}
        maxLength="16"
        onValueChange={(displayValue, _, values) => {
          // intlConfig is already formatting the display and it is being returned
          setInputDisplay(displayValue);
          setInputValue(getRawCurrencyValue(values.float || 0)); // getRawCurrencyValue handles token minor_unit, so let's keep it around
        }}
        onBlur={() => {
          if (!isTouched) {
            setIsTouched(true);
          }
        }}
      />
      <FormControl isInvalid={error}>
        {error ? <FormErrorMessage>{error}</FormErrorMessage> : null}
      </FormControl>
    </div>
  );
};

export default CurrencyInput;
